import React, { useCallback, useState } from "react";
import { useFormikContext } from "formik";
import LinearProgress from "@mui/material/LinearProgress";
import Box from "@mui/material/Box";

import {
  formatAssetInput,
  buildAssetPatch,
  buildAssetBaseValues,
} from "../../../../utils/asset";
import useNotification from "../../../../hooks/notification";
import CaptureActionsScrubber from "../../../../components/capture/CaptureActionsScrubber";
import FormFields from "../../../../views/capture/FormFields";
import TransferModal from "../../../../views/asset/TransferModal";
import { useAssetsCollection } from "../../../../contexts/assetsCollection";
import { useProjectInventory } from "../../../../contexts/projectInventory";
import { usePermissions } from "../../../../contexts/permissions";

const styles = {
  stickyContainer: {
    width: "100%",
    position: "sticky",
    bottom: "10px",
    left: 0,
    zIndex: 99,
    padding: "0",
    backgroundColor: "transparent",
    px: 0,
  },
};

export default function FormPage({
  type,
  currentAsset,
  displayClient,
  displayProject,
  displayWarehouse,
  onCloseModal,
  handeRefetch,
  showOnlySave = false,
  hideAddNew = false,
  autoCloseOnSave = false,
  onAfterSave,
  openDuplicatedAsset,
  fullWidth,
}) {
  const { can } = usePermissions();
  const formProps = useFormikContext();
  const { notifyError, notifySuccess } = useNotification();
  const [openTransfer, setOpenTransfer] = useState(false);

  const {
    projectId,
    loading,
    loadingMutation,
    updateAsset,
    createAsset,
    createImage,
    removeAsset,
  } = useAssetsCollection();

  const { updateInventoryAsset } = useProjectInventory();
  const canWrite = can("assets.write");
  const canRemove = can("assets.remove");

  const handleSubmitAction = ({
    variables,
    setSubmitting,
    setStatus,
    setErrors,
    submitAction,
    handleSubmit,
    onSuccessAction,
    rejectOnConflict = false,
  }) => {
    handleSubmit();
    setSubmitting(true);

    return submitAction({
      variables: {
        ...variables,
        input: formatAssetInput(variables.input),
        baseValues: variables.baseValues ? formatAssetInput(variables.baseValues) : undefined,
      },
      onSuccess: ({ asset, conflict }) => {
        if (asset) updateInventoryAsset(asset.id, asset);
        setSubmitting(false);
        setStatus({ success: !conflict });
        if (conflict) {
          return;
        }
        if (onSuccessAction) {
          onSuccessAction(asset);
        }
      },
      onFailure: (errors) => {
        setStatus({ success: false });
        setErrors(errors);
        notifyError(errors);
        setSubmitting(false);
      },
    }).then((response) => {
      const payload = response?.data ? Object.values(response.data)[0] : response;
      if (!response || payload?.errors?.length) {
        return Promise.reject(payload?.errors || false);
      }
      if (rejectOnConflict && payload?.conflict) {
        return Promise.reject(payload.conflict);
      }
      return response;
    });
  };

  const submitUpdateAsset =
    ({ values, ...props }, options = {}) =>
    async () => {
      const patch = buildAssetPatch(values, formProps.initialValues);
      if (Object.keys(patch).length === 0) {
        notifySuccess("Asset was updated");
        return;
      }
      const variables = {
        id: currentAsset?.id,
        input: patch,
        baseValues: buildAssetBaseValues(patch, formProps.initialValues),
        expectedVersion: currentAsset?.lockVersion,
      };
      return handleSubmitAction({
        ...props,
        variables,
        submitAction: updateAsset,
        rejectOnConflict: options.rejectOnConflict,
        onSuccessAction: () => {
          if (!autoCloseOnSave) {
            return;
          }
          if (onAfterSave) {
            onAfterSave();
          } else if (handeRefetch) {
            handeRefetch();
          }
          if (onCloseModal) {
            onCloseModal();
          }
        },
      });
    };

  const submitCreateAsset =
    ({ values, ...props }) =>
    async () => {
      await submitUpdateAsset(formProps, { rejectOnConflict: true })({
        values,
        ...props,
      });
      const variables = { projectId, input: {} };
      return handleSubmitAction({ ...props, variables, submitAction: createAsset });
    };

  const submitDuplicateAsset =
    ({ values, ...props }) =>
    async () => {
      // Persist any in-progress edits to the original first. If this conflicts,
      // the rejection propagates and we never create a duplicate (no orphans).
      await submitUpdateAsset(formProps, { rejectOnConflict: true })({
        values,
        ...props,
      });
      // The server clones every copyable field off the persisted source asset
      // (barcode/photos excluded), so the client sends only the source id.
      const variables = {
        projectId: currentAsset.project.id,
        input: {},
        sourceAssetId: currentAsset.id,
      };
      return handleSubmitAction({ ...props, variables, submitAction: createAsset }).then(
        (response) => {
          const newAsset = response?.data?.createAsset?.asset;
          if (openDuplicatedAsset && newAsset) {
            openDuplicatedAsset(newAsset);
          }
          return response;
        }
      );
    };

  const submitRemoveAsset = () => {
    return removeAsset({
      variables: { id: currentAsset?.id },
      onSuccess: ({ asset }) => {
        notifySuccess("Asset was removed");
      },
    });
  };

  const toggleTransfer = useCallback(() => {
    setOpenTransfer((v) => !v);
  }, [setOpenTransfer]);

  return (
    <form
      id="assetForm"
      className="d-flex flex-column flex-grow-1 justify-content-between justify-content-md-start"
      style={{ position: "relative" }}
    >
      {loading && (
        <Box py={1}>
          <LinearProgress className="mt-2" />
        </Box>
      )}
      <FormFields
        type={type}
        asset={currentAsset}
        displayClient={displayClient}
        displayProject={displayProject}
        displayWarehouse={displayWarehouse}
        loading={loadingMutation}
        updateAsset={updateAsset}
        createImage={createImage}
        setErrors={formProps.setErrors}
        errors={formProps.errors}
        fullWidth={fullWidth}
      />
      <Box mb={2} />
      <Box sx={styles.stickyContainer}>
        <CaptureActionsScrubber
          disabled={formProps.isSubmitting}
          onSave={submitUpdateAsset(formProps)}
          disableSave={!currentAsset?.id}
          onCreate={submitCreateAsset(formProps)}
          onDuplicate={submitDuplicateAsset(formProps)}
          projectId={projectId}
          onCloseModal={onCloseModal}
          onRemove={submitRemoveAsset}
          onTransfer={toggleTransfer}
          handeRefetch={handeRefetch}
          canWrite={canWrite}
          canRemove={canRemove}
          showOnlySave={showOnlySave}
          hideAddNew={hideAddNew}
        />
        {openTransfer && (
          <TransferModal
            asset={currentAsset}
            handeRefetch={handeRefetch}
            onClose={toggleTransfer}
          />
        )}
      </Box>
    </form>
  );
}
